import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { SharedUserContextService } from '@backbase/shared/feature/user-context';

export const DEV_TOOLS_PREVENTION_ENABLED = new InjectionToken<boolean>('DEV_TOOLS_PREVENTION_ENABLED');

@Injectable({
  providedIn: 'root',
})
export class DevToolsPreventionService implements OnDestroy {
  private initialized = false;
  private detectionHandled = false;
  private devToolsCheckIntervalId: number | null = null;
  private devToolsConsoleProbeIntervalId: number | null = null;
  private readonly listenerEntries: Array<{ type: string; handler: EventListenerOrEventListenerObject }> = [];
  private readonly consoleProbe = this.buildConsoleProbe();

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(DEV_TOOLS_PREVENTION_ENABLED) private readonly isEnabled: boolean,
    private readonly userContextService: SharedUserContextService,
    private readonly oAuthService: OAuthService,
  ) { }

  initialize(): void {
    if (!this.isEnabled || this.initialized) {
      return;
    }

    this.initialized = true;
    this.disableRightClick();
    this.disableKeyboardShortcuts();
    this.disableTextSelection();
    this.disableDragAndDrop();
    this.detectDevToolsBySize();
    this.detectDevToolsByConsoleProbe();
  }

  ngOnDestroy(): void {
    this.cleanup();
  }

  private disableRightClick(): void {
    this.addDocumentListener('contextmenu', (event: Event) => {
      event.preventDefault();
    });
  }

  private disableKeyboardShortcuts(): void {
    this.addDocumentListener('keydown', (event: Event) => {
      const keydownEvent = event as KeyboardEvent;
      const key = keydownEvent.key.toLowerCase();
      const isMetaCombo = keydownEvent.ctrlKey || keydownEvent.metaKey;
      const isShiftCombo = keydownEvent.shiftKey;

      const shouldBlock =
        keydownEvent.key === 'F12' ||
        (isMetaCombo && isShiftCombo && ['i', 'j', 'c'].includes(key)) ||
        (isMetaCombo && ['u', 's'].includes(key));

      if (shouldBlock) {
        keydownEvent.preventDefault();
      }
    });
  }

  private detectDevToolsBySize(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.devToolsCheckIntervalId = window.setInterval(() => {
      if (this.detectionHandled) {
        return;
      }

      if (this.isDevToolsOpenByWindowDimensions()) {
        this.handleDevToolsDetected();
      }
    }, 500);
  }

  private detectDevToolsByConsoleProbe(): void {
    if (typeof window === 'undefined') {
      return;
    }

    this.devToolsConsoleProbeIntervalId = window.setInterval(() => {
      if (this.detectionHandled) {
        return;
      }
      // This getter pattern only triggers when DevTools console actively inspects the object.
      // eslint-disable-next-line no-console
      console.debug(this.consoleProbe);
    }, 1000);
  }

  private isDevToolsOpenByWindowDimensions(): boolean {
    const threshold = 120;
    const widthDiff = window.outerWidth - window.innerWidth;
    const heightDiff = window.outerHeight - window.innerHeight;
    const compressedViewport = window.outerWidth > 0 && window.innerWidth / window.outerWidth < 0.87;

    return widthDiff > threshold || heightDiff > threshold || compressedViewport;
  }

  private buildConsoleProbe(): object {
    const probe = new Image();
    Object.defineProperty(probe, 'id', {
      configurable: true,
      get: () => {
        this.handleDevToolsDetected();
        return 'devtools-detected';
      },
    });
    return probe;
  }

  private handleDevToolsDetected(): void {
    if (this.detectionHandled) {
      return;
    }

    this.detectionHandled = true;
    this.cleanup();
    this.logoutIfAuthenticated();
    this.renderRestrictedMessage();
  }

  private logoutIfAuthenticated(): void {
    const hasValidAccessToken = this.oAuthService.hasValidAccessToken();
    const hasValidIdToken = this.oAuthService.hasValidIdToken();

    if (!hasValidAccessToken && !hasValidIdToken) {
      return;
    }

    this.userContextService.logout();
  }

  private renderRestrictedMessage(): void {
    if (!this.document.body) {
      return;
    }

    const existingOverlay = this.document.getElementById('dev-tools-restricted-overlay');
    if (existingOverlay) {
      return;
    }

    const overlay = this.document.createElement('div');
    overlay.id = 'dev-tools-restricted-overlay';
    overlay.setAttribute(
      'style',
      [
        'position: fixed',
        'inset: 0',
        'display: flex',
        'align-items: center',
        'justify-content: center',
        'padding: 24px',
        'background: #f5f7fa',
        'font-family: "Open Sans", system-ui, -apple-system, sans-serif',
        'z-index: 2147483647',
      ].join('; '),
    );

    const card = this.document.createElement('div');
    card.setAttribute(
      'style',
      [
        'max-width: 440px',
        'width: 100%',
        'background: #ffffff',
        'border-radius: 12px',
        'box-shadow: 0 8px 24px rgba(0, 51, 170, 0.08), 0 1px 3px rgba(0, 0, 0, 0.06)',
        'padding: 40px 32px',
        'text-align: center',
      ].join('; '),
    );

    const logo = this.document.createElement('img');
    logo.setAttribute('src', 'assets/images/sbk-logo.png');
    logo.setAttribute('alt', 'Stanbic Bank');
    logo.setAttribute('style', 'width: 45%; height: auto; display: block; margin: 0 auto 24px;');
    logo.addEventListener('error', () => {
      logo.style.display = 'none';
    });

    const illustration = this.document.createElement('img');
    illustration.setAttribute('src', '/assets/images/bb-illustration-ui/ic_asset_lock_error.svg');
    illustration.setAttribute('alt', 'asset lock error');
    illustration.setAttribute('width', '240');
    illustration.setAttribute('height', '240');
    illustration.setAttribute('style', 'display: block; margin: 0 auto 16px;');

    const title = this.document.createElement('h1');
    title.textContent = 'Action restricted';
    title.setAttribute(
      'style',
      [
        'margin: 0 0 12px',
        'color: #0a1a3a',
        'font-size: 20px',
        'font-weight: 600',
        'line-height: 1.3',
      ].join('; '),
    );

    const body = this.document.createElement('p');
    body.textContent = 'This session was ended due to restricted browser activity. For assistance call customer care on +254711068888 or email: ibkenya@stanbic.com.';
    body.setAttribute(
      'style',
      [
        'margin: 0 0 28px',
        'color: #4a5568',
        'font-size: 14px',
        'line-height: 1.5',
      ].join('; '),
    );

    const button = this.document.createElement('button');
    button.type = 'button';
    button.textContent = 'Refresh';
    button.setAttribute(
      'style',
      [
        'padding: 10px 28px',
        'font-size: 14px',
        'font-weight: 600',
        'font-family: inherit',
        'cursor: pointer',
        'border: none',
        'border-radius: 6px',
        'background: #0033aa',
        'color: #ffffff',
        'transition: background 0.15s ease',
      ].join('; '),
    );
    button.addEventListener('mouseenter', () => {
      button.style.background = '#002280';
    });
    button.addEventListener('mouseleave', () => {
      button.style.background = '#0033aa';
    });
    button.addEventListener('click', () => window.location.reload());

    card.appendChild(logo);
    card.appendChild(illustration);
    card.appendChild(title);
    card.appendChild(body);
    card.appendChild(button);
    overlay.appendChild(card);
    this.document.body.appendChild(overlay);
  }

  private disableTextSelection(): void {
    this.addDocumentListener('selectstart', (event: Event) => {
      event.preventDefault();
    });
  }

  private disableDragAndDrop(): void {
    this.addDocumentListener('dragstart', (event: Event) => {
      event.preventDefault();
    });
  }

  private addDocumentListener(type: string, handler: EventListenerOrEventListenerObject): void {
    this.document.addEventListener(type, handler);
    this.listenerEntries.push({ type, handler });
  }

  private cleanup(): void {
    this.listenerEntries.forEach(({ type, handler }) => {
      this.document.removeEventListener(type, handler);
    });
    this.listenerEntries.length = 0;

    if (this.devToolsCheckIntervalId !== null) {
      window.clearInterval(this.devToolsCheckIntervalId);
      this.devToolsCheckIntervalId = null;
    }

    if (this.devToolsConsoleProbeIntervalId !== null) {
      window.clearInterval(this.devToolsConsoleProbeIntervalId);
      this.devToolsConsoleProbeIntervalId = null;
    }
  }
}
